import React, { useEffect, useState, useRef } from 'react';
import cx from 'classnames';
import { Layout, Button } from 'antd';
import Helmet from 'react-helmet';
import history from '../modules/history';
import app_params from '../../../config/params';
import EditorHeader from '../components/editor/EditorHeader';
import { tutorialBlocks } from '../components/editor/blocks/tutorialBlocks';
import {
  BlockDrawer, FontDrawer, ColorDrawer,
  SeoDrawer, AnalyticsDrawer,
} from '../components/editor/drawers';

import { GallerySelectModal } from '../components/editor/GallerySelectModal';
import { IconChooserModal } from '../components/editor/IconChooserModal';
import { LoadingSpinner } from '../presentation/LoadingSpinner';
import { EditorPageContainer } from '../components/editor/EditorPageContainer';
import {
  getVariantInfo,
  updateFont,
  updateColor,
  addBlock,
  addBlocks,
  renameBlock,
  reorderBlock,
  deleteBlock,
  updateBlock,
  updateSeo,
  syncBlock,
  updateAnalytics,
  deleteElement,
  publish,
} from '../actions/editor';
import { getPages } from '../actions/page';
import { freeze } from '../modules/freeze';
import { updatePageVariantAttr, updatePageVariantTutorialUpdateLocal } from '../actions/page';
import {
  PlusOutlined,
  BlockOutlined,
} from '@ant-design/icons';
import { analytics as ph_analytics } from '../modules/analytics';


export const Editor = ({
  page,
  variantUrlsafe,
  variant,
  tutorial,
  gallery,
  token,
  iconChooser,
  dispatch,
  networkError,
}) => {

  const loadContents = () => {
    dispatch(getVariantInfo(variantUrlsafe));

    // When the editor is refreshed or launched directly within the browser,
    // page store is not cached locally and hence need to be fetched.
    (page.status === 'init') && dispatch(getPages());
  };

  const populateTutorialBlocks = () => {
    const { pageUrlsafe = null } = variant;

    dispatch((addBlocks(variantUrlsafe, 0, tutorialBlocks)));
    dispatch((updatePageVariantTutorialUpdateLocal( pageUrlsafe, variantUrlsafe, true)));
    dispatch((updatePageVariantAttr( pageUrlsafe, variantUrlsafe,
      [{ key: 'tutorialPopulated', type: 'bool', val: true }]
    )));
  };

  // Run once in the beginning - empty array
  useEffect(() => {
    ph_analytics.trackView(`/editor/${variantUrlsafe}`);
    loadContents();
  }, []);


  useEffect(() => {
    if (networkError === false) { // coming out of a network error
      loadContents();
    }
  }, [networkError]);


  // User picks a different variant from the dropdown
  useEffect(() => {
    dispatch(getVariantInfo(variantUrlsafe));
  }, [variantUrlsafe]);


  // // Load tutorial blocks
  // useEffect(() => {
  //   const { tutorialPopulated = null, blocks = [] } = variant;
  //   if (tutorial && !tutorialPopulated && (blocks && blocks.length === 0)) {
  //     populateTutorialBlocks();
  //   }
  // }, [tutorial, variant]);


  const buildFontURL = (font) => {
    if( 'family' in font && 'weight' in font) {
      const { family: fontFamily, weight: fontWeight } = font;
      let fontInfo = "";
      fontFamily.forEach((ftype, index) => {
        fontInfo += ftype.replace(/ /g, '+');
        fontInfo += ':';
        fontInfo += fontWeight[index];
        if (index < 2) {
          fontInfo += '|';
        }
      });

      const fontUrl = `https://fonts.googleapis.com/css?family=${fontInfo}`;
      return fontUrl;
    }
    return "";
  };


  const [menuKey, setMenuKey] = useState(null);
  const onDrawerClose = () => {
    setMenuKey(null);
  };
  const onMenuChangeKey = (key) => {
    setMenuKey(key);
  };


  const [galleryModal, setGalleryModal] = useState({ visible: false, sender: null });
  const onGalleryClose = () => {
    setGalleryModal({ visible: false, sender: null });
  };
  const onInvokeGallery = (blockUrlsafe, address, selectedProp, renderSize) => {
    setGalleryModal({
      visible: true,
      sender: {
        blockUrlsafe,
        address,
        selectedProp,
        renderSize,
      },
    });
  };


  const [iconModal, setIconModal] = useState({
    visible: false,
    sender: null,
  });
  const onIconChooserClose = () => {
    this.setIconModal({ visible: false, sender: null });
  };
  const onInvokeIconChooser = (blockUrlsafe, address, selectedProp) => {
    this.setIconModal({
      visible: true,
      sender: {
        blockUrlsafe,
        address,
        selectedProp,
      },
    });
  };


  const onBlocksReorder = (event, previousIndex, nextIndex) => {
    if (previousIndex !== nextIndex) {
      dispatch(reorderBlock(variantUrlsafe, previousIndex, nextIndex));
    }
  };

  const onBlockDelete = (blockUrlsafe) => {
    dispatch(deleteBlock(variantUrlsafe, blockUrlsafe));
  };

  const onBlockRename = (blockUrlsafe, newName) => {
    dispatch(renameBlock(variantUrlsafe, blockUrlsafe, newName));
  };

  const onBlockInsert = (index, block) => {
    const frozen = freeze(block.jsx);
    dispatch(addBlock(
      variantUrlsafe,
      index,
      block.name,
      frozen,
    ));
  };

  const onSeoAttributeChange = (key, val) => {
    dispatch(updateSeo(variantUrlsafe, key, val));
  };

  const onAnalyticsIdChange = (key, val) => {
    dispatch(updateAnalytics(variantUrlsafe, key, val));
  };

  const onFontSelect = (font) => {
    dispatch(updateFont(variantUrlsafe, font));
  };

  const onColorSelect = (color) => {
    dispatch(updateColor(variantUrlsafe, color));
  };

  const onBlockUpdate = (blockUrlsafe, address, updatedProps) => {
    dispatch(updateBlock(variantUrlsafe, blockUrlsafe, address, updatedProps));
  };

  const onBlockSync = (blockUrlsafe, content, synctill) => {
    dispatch(syncBlock(variantUrlsafe, blockUrlsafe, content, synctill));
  };

  const onElementDelete = (blockUrlsafe, address) => {
    dispatch(deleteElement(variantUrlsafe, blockUrlsafe, address));
  };

  const pageContainerRef = useRef(null);
  const onPublish = () => {
    const pageHtml = pageContainerRef.current.getPageHtml();
    dispatch(publish(variantUrlsafe, pageHtml));
  };

  const blockDrawerRef = useRef();
  const showAddBlockOptions = () => {
    blockDrawerRef.current.onAddMoreBlockBtnClick();
  };


  const fallbackSrc = (renderSize) => {
    if (renderSize === 'lg') {
      return require('assets/media/editor/image-not-found-1920.png');
    }
    if (renderSize === 'md') {
      return require('assets/media/editor/image-not-found-1280.png');
    }
    if (renderSize === 'sm') {
      return require('assets/media/editor/image-not-found-640.png');
    }
    return require('assets/media/editor/image-not-found-200.png');
  };


  const srcForMedia = (mediaUrlsafe, renderSize) => {

    const { medias } = gallery;
    const media = medias.find(m => m.urlsafe === mediaUrlsafe);

    if (typeof media === 'undefined') {
      return fallbackSrc(renderSize);
    }

    let src = null;
    if ('resolutions' in media) {
      const { resolutions } = media;
      if (renderSize === 'lg') {
        if ('large' in resolutions && resolutions.large !== null && 'path' in resolutions.large) {
          src = resolutions.large.path;
        }
      }
      if (renderSize === 'md') {
        if ('medium' in resolutions && resolutions.medium !== null && 'path' in resolutions.medium) {
          src = resolutions.medium.path;
        }
      }
      if (renderSize === 'sm') {
        if ('small' in resolutions && resolutions.small !== null && 'path' in resolutions.small) {
          src = resolutions.small.path;
        }
      }
      if (renderSize === 'thumb') {
        if ('thumb' in resolutions && resolutions.thumb !== null && 'path' in resolutions.thumb) {
          src = resolutions.thumb.path;
        }
      }
    }
    if (src === null) {
      return fallbackSrc(renderSize);
    }
    return `${app_params.GAE_ENDPOINT.MEDIA_URL}${src}`;
  };


  const changesNotPublished = () => {
    const { blocks = null } = variant;
    if (blocks) {
      const last_published = variant.published;
      const last_updated = variant.blocks.reduce((accumulator, block)=>(
        Math.max(accumulator, block.updated)
      ), variant.updated);
      if (last_published < last_updated) {
        return true;
      }
    }
    return false;
  };

  const busySyncing = () => {
    const { blocks = null } = variant;
    if (blocks) {
      const ahead = variant.blocks.reduce((accumulator, block)=>(
        Math.max(accumulator, block.localedits - block.synctill)
      ), 0);
      if (ahead > 0) {
        return true;
      }
    }
    return false;
  };


  const {
    status: variantStatus = null,
    blocks = [],
    font = {}, color = {},
    seo = {}, analytics = {},
  } = variant;
  const isLoading = variantStatus === 'loading';
  const isPublishing = variantStatus === 'publishing';
  const isEmpty = (blocks.length === 0);

  const emptyState = (
    <div className="container">
      <div className="row mt-5">
        <div className="col-12 col-sm-10 col-md-8 col-lg-6 text-center mx-auto">
          <h3 className="my-3">Empty page</h3>
          <p className="mb-4 text-secondary">Build your page with block templates.</p>
          <Button
            type="primary" size="large" icon={<BlockOutlined />}
            onClick={showAddBlockOptions}
          >
            Add blocks
          </Button>
        </div>
      </div>
    </div>
  );


  const drawers = (
    <React.Fragment>
      <BlockDrawer
        ref={blockDrawerRef}
        visible={menuKey === 'block'}
        onClose={onDrawerClose}
        currentBlocks={blocks}
        onBlocksReorder={onBlocksReorder}
        onBlockDelete={onBlockDelete}
        onBlockRename={onBlockRename}
        onBlockInsert={onBlockInsert}
        destroyOnClose={true}
      />
      <FontDrawer
        visible={menuKey === 'style:font'}
        onClose={onDrawerClose}
        currentFont={font}
        fontSelected={onFontSelect}
        destroyOnClose={true}
      />
      <ColorDrawer
        visible={menuKey === 'style:color'}
        onClose={onDrawerClose}
        currentColor={color}
        colorSelected={onColorSelect}
        destroyOnClose={true}
      />
      <SeoDrawer
        seo={seo}
        visible={menuKey === 'meta:seo'}
        onClose={onDrawerClose}
        onAttributeChange={onSeoAttributeChange}
        destroyOnClose={true}
      />
      <AnalyticsDrawer
        analytics={analytics}
        visible={menuKey === 'meta:analytics'}
        onClose={onDrawerClose}
        onAnalyticsIdChange={onAnalyticsIdChange}
        destroyOnClose={true}
      />
    </React.Fragment>
  );

  return (
    <Layout
      className={cx('app__editor', {
        loading: isLoading,
        publishing: isPublishing,
      })}
    >
      <Helmet>
        <link href={buildFontURL(font)} rel="stylesheet" />
      </Helmet>

      <EditorHeader
        page={page}
        currentKey={menuKey}
        variant={variant}
        changeKey={onMenuChangeKey}
        goPagesHome={() => { history.push('/pages'); }}
        goToVariant={(newVariantUrlsafe) => {
          if(variantUrlsafe !== newVariantUrlsafe) {
            history.push('/editor/' + newVariantUrlsafe)
          }
        }}
        onPublish={onPublish}
        isLoading={isLoading}
        isPublishing={isPublishing}
        busySyncing={busySyncing()}
        changesNotPublished={changesNotPublished()}
        variantUrlsafe={variantUrlsafe}
      />

      <Layout.Content className="editor__content">
        {/*<EditorThemeProvider>*/}
          <EditorPageContainer
            ref={pageContainerRef}
            blocks={blocks}
            onBlockSync={onBlockSync}
            onBlockUpdate={onBlockUpdate}
            onInvokeGallery={onInvokeGallery}
            onInvokeIconChooser={onInvokeIconChooser}
            onElementDelete={onElementDelete}
            srcForMedia={srcForMedia}
            color={color}
            font={font}
          />
        {/*</EditorThemeProvider>*/}

        { isLoading ? (<LoadingSpinner />) : null }

        { isEmpty && !isLoading ? emptyState : null }

        { drawers }

        <GallerySelectModal
          gallery={gallery}
          token={token}
          visible={galleryModal.visible}
          sender={galleryModal.sender}
          onClose={onGalleryClose}
          dispatch={dispatch}
          onBlockUpdate={onBlockUpdate}
        />

        <IconChooserModal
          iconChooser={iconChooser}
          sender={iconModal.sender}
          visible={iconModal.visible}
          onClose={onIconChooserClose}
          onBlockUpdate={onBlockUpdate}
        />

      </Layout.Content>
    </Layout>
  );
};



// WEBPACK FOOTER //
// ./scripts/presentation/Editor.jsx